import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Loader2 } from "lucide-react";

export default function Account() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const [twofa, setTwofa] = useState(null); // {qr_png, secret}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = !!user?.twofa_enabled;

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setSavingPw(false); }
  };

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup", {}); setTwofa(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const activate = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication is now active");
      setTimeout(() => window.location.reload(), 600);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-2xl" data-testid="account-page">
      <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Account</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-1">Security</h1>
      <p className="text-sm text-gray-500 mb-6">Signed in as <b>{user?.email}</b> · <span className="uppercase tracking-wider text-xs">{user?.role}</span></p>

      {/* Password */}
      <div className="bg-white border border-gray-200 rounded-md p-5 mb-6">
        <div className="flex items-center gap-2 mb-4"><KeyRound className="w-4 h-4 text-[#01ADEF]" /><h2 className="font-display text-lg font-bold">Change password</h2></div>
        <form onSubmit={changePassword} className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider font-semibold">Current password</Label><Input data-testid="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-md mt-1" required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider font-semibold">New password</Label><Input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-md mt-1" required /></div>
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Confirm new</Label><Input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="rounded-md mt-1" required /></div>
          </div>
          <Button data-testid="pw-save" type="submit" disabled={savingPw} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}</Button>
        </form>
      </div>

      {/* 2FA */}
      <div className={`bg-white border border-gray-200 rounded-md p-5 ${enabled ? "" : "opacity-95"}`} data-testid="account-2fa">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className={`w-4 h-4 ${enabled ? "text-[#059669]" : "text-gray-400"}`} /><h2 className="font-display text-lg font-bold">Two-factor authentication</h2>
          {enabled
            ? <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46]">ACTIVE</span>
            : <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">INACTIVE</span>}
        </div>
        {enabled ? (
          <p className="text-sm text-gray-600">Your account is protected with an authenticator app. To reset it, ask an administrator.</p>
        ) : !twofa ? (
          <div className="text-sm text-gray-500">
            <p className="mb-3">Add an extra layer of security using an authenticator app (Google Authenticator, Authy).</p>
            <Button data-testid="account-2fa-start" onClick={startSetup} disabled={busy} variant="outline" className="rounded-full border-gray-300">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set up 2FA"}</Button>
          </div>
        ) : (
          <form onSubmit={activate} className="space-y-3">
            <div className="flex justify-center"><img src={twofa.qr_png} alt="2FA QR" data-testid="account-2fa-qr" className="w-40 h-40 border border-gray-200 rounded-md" /></div>
            <p className="text-[11px] text-gray-500 text-center">Manual key: <span className="font-mono font-bold break-all">{twofa.secret}</span></p>
            <Input data-testid="account-2fa-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} className="rounded-full text-center tracking-[0.3em] font-mono" />
            <Button data-testid="account-2fa-activate" type="submit" disabled={busy || code.length < 6} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate 2FA"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
